import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { listCampaigns, createCampaign } from '@/services/campaigns';
import { updateCreative } from '@/services/creatives';
import type { Campaign } from '@/types/api';

interface AssignCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  creativeId: number | null;
  creativeName: string;
  onAssigned?: () => void;
}

export default function AssignCampaignModal({
  isOpen,
  onClose,
  creativeId,
  creativeName,
  onAssigned,
}: AssignCampaignModalProps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [error, setError] = useState('');

  // New campaign inline
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listCampaigns({ limit: 100 })
      .then((page) => setCampaigns(page.items.filter((c) => c.id != null)))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load campaigns'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setShowCreate(false);
      setNewName('');
      return;
    }
    setError('');
    load();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, load, onClose]);

  async function handleAssign(campaignId: number) {
    if (creativeId == null) return;
    setAssigningId(campaignId);
    try {
      await updateCreative(creativeId, { assigned_campaign_ids: [campaignId] });
      onAssigned?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to assign');
    } finally {
      setAssigningId(null);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const c = await createCampaign({ name: newName.trim() });
      load();
      setShowCreate(false);
      setNewName('');
      if (c.id != null) handleAssign(c.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  }

  if (!isOpen) return null;

  const modalContent = (
    <div className="aspectly:fixed aspectly:inset-0 aspectly:z-[9999] aspectly:flex aspectly:items-center aspectly:justify-center aspectly:p-4">
      <div className="aspectly:absolute aspectly:inset-0 aspectly:bg-on-surface/30 aspectly:backdrop-blur-sm" onClick={onClose} />
      <div className="aspectly:relative aspectly:z-10 aspectly:w-full aspectly:max-w-lg aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:shadow-2xl aspectly:overflow-hidden aspectly:border aspectly:border-outline-variant/10">
        {/* Header */}
        <div className="aspectly:flex aspectly:items-center aspectly:justify-between aspectly:px-6 aspectly:py-5 aspectly:border-b aspectly:border-outline-variant/10">
          <div>
            <h2 className="aspectly:font-headline aspectly:font-bold aspectly:text-on-surface aspectly:text-lg">Add to Campaign</h2>
            <p className="aspectly:text-sm aspectly:text-on-surface-variant aspectly:mt-0.5 aspectly:truncate">{creativeName}</p>
          </div>
          <button
            onClick={onClose}
            className="aspectly:w-9 aspectly:h-9 aspectly:flex aspectly:items-center aspectly:justify-center aspectly:rounded-full aspectly:hover:bg-surface-container aspectly:text-on-surface-variant aspectly:hover:text-on-surface aspectly:transition-colors aspectly:cursor-pointer"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="aspectly:p-6 aspectly:space-y-3 aspectly:max-h-[60vh] aspectly:overflow-y-auto">
          {error && (
            <div className="aspectly:p-3 aspectly:rounded-lg aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
              {error}
            </div>
          )}

          {/* Create new inline */}
          {showCreate ? (
            <form onSubmit={handleCreate} className="aspectly:flex aspectly:gap-2 aspectly:mb-4">
              <input
                autoFocus
                required
                className="aspectly:flex-1 aspectly:px-3 aspectly:py-2.5 aspectly:bg-surface-container-low aspectly:text-sm aspectly:text-on-surface aspectly:border aspectly:border-primary/40 aspectly:rounded-lg aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/30"
                placeholder="New campaign name…"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <button
                type="submit"
                disabled={creating || !newName.trim()}
                className="aspectly:px-4 aspectly:py-2 aspectly:bg-primary aspectly:text-on-primary aspectly:rounded-lg aspectly:font-bold aspectly:text-sm aspectly:disabled:opacity-50 aspectly:cursor-pointer aspectly:hover:bg-primary-dim aspectly:transition-all"
              >
                {creating ? '…' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreate(false);
                  setNewName('');
                }}
                className="aspectly:px-3 aspectly:py-2 aspectly:text-on-surface-variant aspectly:hover:text-on-surface aspectly:cursor-pointer"
              >
                Cancel
              </button>
            </form>
          ) : (
            <button
              onClick={() => setShowCreate(true)}
              className="aspectly:w-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:gap-2 aspectly:px-4 aspectly:py-3 aspectly:mb-2 aspectly:rounded-xl aspectly:border aspectly:border-dashed aspectly:border-outline-variant/40 aspectly:text-primary aspectly:text-sm aspectly:font-bold aspectly:hover:bg-primary-container/30 aspectly:transition-colors aspectly:cursor-pointer"
            >
              <span className="material-symbols-outlined aspectly:text-lg">add</span>
              Campaign
            </button>
          )}

          {loading ? (
            <div className="aspectly:flex aspectly:justify-center aspectly:py-10">
              <span className="material-symbols-outlined aspectly:animate-spin aspectly:text-primary aspectly:text-4xl">
                progress_activity
              </span>
            </div>
          ) : campaigns.length === 0 ? (
            <p className="aspectly:text-on-surface-variant aspectly:text-sm aspectly:py-4 aspectly:text-center">
              No campaigns yet. Create one above.
            </p>
          ) : (
            <ul className="aspectly:space-y-1.5">
              {campaigns.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    disabled={assigningId != null}
                    onClick={() => handleAssign(c.id!)}
                    className="aspectly:w-full aspectly:text-left aspectly:px-4 aspectly:py-3 aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/20 aspectly:text-on-surface aspectly:text-sm aspectly:font-medium aspectly:hover:bg-primary-container/30 aspectly:hover:border-primary/30 aspectly:transition-all aspectly:cursor-pointer aspectly:disabled:opacity-50 aspectly:flex aspectly:items-center aspectly:justify-between aspectly:gap-2"
                  >
                    <div className="aspectly:flex aspectly:items-center aspectly:gap-3">
                      <span className="material-symbols-outlined aspectly:text-primary aspectly:text-lg">
                        campaign
                      </span>
                      <span className="truncate">{c.name}</span>
                    </div>
                    {assigningId === c.id && (
                      <span className="material-symbols-outlined aspectly:animate-spin aspectly:text-primary aspectly:text-xl aspectly:shrink-0">
                        progress_activity
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
